'use client'

import React, { useState, useCallback, useEffect, useRef } from 'react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Checkbox } from '@/components/ui/checkbox'
import { Badge } from '@/components/ui/badge'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Search, Database, Loader2, Check, ChefHat, Lock } from 'lucide-react'
import type { Ingredient, NutrientValue, Recipe } from '../types'

/**
 * Create/Edit Ingredient Modal
 *
 * Modal for adding or editing ingredients with:
 * - Search sub-recipes (recipes marked as sub-recipe)
 * - Search USDA FoodData Central for ingredients
 * - Basic info (name, quantity, unit, yield, cost)
 * - Nutrients (40+ nutrients per 100g)
 * - Allergens (18 main + 45 may-contain)
 */

// USDA ingredient type from our API
interface USDAIngredient {
  id: string
  fdcId: number
  name: string
  description: string
  dataType: string
  foodCategory: string
  scientificName?: string
  nutrients: Record<string, { quantity: number; unit: string }>
  source: string
}

// All nutrients with their default units
const NUTRIENTS: { name: string; unit: string }[] = [
  { name: 'Energy', unit: 'kcal' },
  { name: 'Total Carbohydrates', unit: 'g' },
  { name: 'Protein', unit: 'g' },
  { name: 'Total Fat', unit: 'g' },
  { name: 'Dietary Fiber', unit: 'g' },
  { name: 'Net Carbohydrates', unit: 'g' },
  { name: 'Calcium', unit: 'mg' },
  { name: 'Sodium', unit: 'mg' },
  { name: 'Iron', unit: 'mg' },
  { name: 'Potassium', unit: 'mg' },
  { name: 'Saturated Fat', unit: 'g' },
  { name: 'Trans Fat', unit: 'g' },
  { name: 'Total Sugar', unit: 'g' },
  { name: 'Cholesterol', unit: 'mg' },
  { name: 'Vitamin D', unit: 'mcg' },
  { name: 'Phosphorus', unit: 'mg' },
  { name: 'Sugar Alcohol', unit: 'g' },
  { name: 'Added Sugar', unit: 'g' },
  { name: 'Salt', unit: 'g' },
  { name: 'Magnesium', unit: 'mg' },
  { name: 'Pantothenic acid', unit: 'mg' },
  { name: 'Polyunsaturated Fat', unit: 'g' },
  { name: 'Manganese', unit: 'mg' },
  { name: 'Vitamin A', unit: 'mcg' },
  { name: 'Vitamin E', unit: 'mg' },
  { name: 'Vitamin C', unit: 'mg' },
  { name: 'Vitamin B6', unit: 'mg' },
  { name: 'Zinc', unit: 'mg' },
  { name: 'Thiamin', unit: 'mg' },
  { name: 'Niacin', unit: 'mg' },
  { name: 'Monounsaturated Fat', unit: 'g' },
  { name: 'Copper', unit: 'mg' },
  { name: 'Selenium', unit: 'mcg' },
  { name: 'Riboflavin', unit: 'mg' },
  { name: 'Folate', unit: 'mcg' },
  { name: 'Vitamin B12', unit: 'mcg' },
  { name: 'Vitamin K', unit: 'mcg' },
  { name: 'Caffeine', unit: 'mg' },
]

// Main allergens (18)
const ALLERGENS = [
  'Dairy',
  'Fish',
  'Gluten',
  'Treenut',
  'Sesame',
  'Oats',
  'Mustard',
  'Natural latex',
  'Lupin',
  'Lentil',
  'Peanut',
  'Soy',
  'Sulfur Dioxide or Sulphites',
  'Egg',
  'Celery',
  'Molluscs',
  'Crustacean',
  'Casein',
]

// May contain allergens (45)
const MAY_CONTAIN_ALLERGENS = [
  'Banana',
  'Monosodium Glutamate',
  'Red Chilli',
  'Litchee',
  'Papaya',
  'Food Color',
  'Apple',
  'Wine',
  'Sunflower Seed',
  'Cocoa',
  'Kiwi',
  'Cinnamon',
  'Citrus',
  'Poultry Meat',
  'Garlic',
  'Caffeine',
  'Cucumber',
  'Fig',
  'Sago',
  'Pumpkin',
  'Mutton',
  'Brinjal',
  'Chickpea',
  'Food Additive',
  'Corn',
  'Ladys Finger',
  'Poppy',
  'Coconut',
  'Yeast',
  'Red Meat',
  'Tomato',
  'Tamarind',
  'Mushroom',
  'Masoor Dal',
  'Strawberry',
  'Turmeric',
  'Green Peas',
  'Asafoetida',
  'Green Grapes',
  'White Meat',
  'Mango',
  'Pineapple',
  'Carrot',
  'Beetroot',
  'Ginger',
]

// Unit options
const UNITS = ['G', 'KG', 'ML', 'L', 'TBSP', 'TSP', 'CUP', 'PIECE']

/**
 * Compute per-100g nutrition from a sub-recipe's per_recipe_total and total_yield_grams.
 */
function computePer100gFromRecipe(recipe: Recipe): Record<string, NutrientValue> {
  const nutrients: Record<string, NutrientValue> = {}
  const totalYield = recipe.nutrition?.total_yield_grams || recipe.serving?.total_yield_grams || 0
  if (totalYield <= 0) return nutrients

  const perRecipeTotal = recipe.nutrition?.per_recipe_total || {}
  Object.entries(perRecipeTotal).forEach(([name, value]) => {
    nutrients[name] = {
      quantity: (value.quantity / totalYield) * 100,
      unit: value.unit,
    }
  })
  return nutrients
}


interface CreateIngredientModalProps {
  isOpen: boolean
  ingredient: Ingredient
  isEditing: boolean
  currentRecipeId?: string | null
  onClose: () => void
  onSave: () => void
  onChange: (updates: Partial<Ingredient>) => void
}

export function CreateIngredientModal({
  isOpen,
  ingredient,
  isEditing,
  currentRecipeId,
  onClose,
  onSave,
  onChange,
}: CreateIngredientModalProps) {
  const [activeTab, setActiveTab] = useState('search')
  const [nutrientSearch, setNutrientSearch] = useState('')
  const [allergenSearch, setAllergenSearch] = useState('')
  const [allergenTab, setAllergenTab] = useState<'allergens' | 'maycontain'>('allergens')

  // Database search state
  const [dbSearchQuery, setDbSearchQuery] = useState('')
  const [dbSearchResults, setDbSearchResults] = useState<USDAIngredient[]>([])
  const [dbSearchLoading, setDbSearchLoading] = useState(false)
  const [selectedDbIngredient, setSelectedDbIngredient] = useState<USDAIngredient | null>(null)

  // Sub-recipe search state
  const [subRecipeSearch, setSubRecipeSearch] = useState('')
  const [subRecipeResults, setSubRecipeResults] = useState<Recipe[]>([])
  const [subRecipeLoading, setSubRecipeLoading] = useState(false)
  const [selectedSubRecipe, setSelectedSubRecipe] = useState<Recipe | null>(null)
  const subRecipeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // User ingredients from localStorage
  const [userIngredients, setUserIngredients] = useState<Ingredient[]>([])
  const [userIngredientSearch, setUserIngredientSearch] = useState('')

  const isSubRecipeIngredient = ingredient.source === 'SUB_RECIPE'

  // Load user ingredients from localStorage
  useEffect(() => {
    if (isOpen) {
      const saved = localStorage.getItem('recipe_builder_user_ingredients')
      if (saved) {
        try {
          setUserIngredients(JSON.parse(saved))
        } catch (e) {
          console.error('Failed to load user ingredients:', e)
        }
      }
    }
  }, [isOpen])

  // Filter user ingredients by search
  const filteredUserIngredients = userIngredients.filter((ing) =>
    ing.name.toLowerCase().includes(userIngredientSearch.toLowerCase())
  )

  // Reset state when modal opens/closes
  useEffect(() => {
    if (isOpen && !isEditing) {
      setActiveTab('search')
      setDbSearchQuery('')
      setDbSearchResults([])
      setSelectedDbIngredient(null)
      setSelectedSubRecipe(null)
      setSubRecipeSearch('')
      setSubRecipeResults([])
      setUserIngredientSearch('')
    } else if (isOpen && isEditing) {
      setActiveTab('basic')
    }
  }, [isOpen, isEditing])

  // Search sub-recipes with debounce
  useEffect(() => {
    if (subRecipeTimerRef.current) {
      clearTimeout(subRecipeTimerRef.current)
    }

    if (!subRecipeSearch || subRecipeSearch.length < 2) {
      setSubRecipeResults([])
      return
    }

    subRecipeTimerRef.current = setTimeout(async () => {
      setSubRecipeLoading(true)
      try {
        const params = new URLSearchParams({
          subRecipesOnly: 'true',
          search: subRecipeSearch,
          pageSize: '20',
        })
        const res = await fetch(`/api/recipe-builder/recipes?${params}`)
        if (res.ok) {
          const { data } = await res.json()
          // Filter out the current recipe to prevent self-reference
          const filtered = (data || []).filter((r: Recipe) => r.id !== currentRecipeId)
          setSubRecipeResults(filtered)
        }
      } catch (error) {
        console.error('Failed to search sub-recipes:', error)
      } finally {
        setSubRecipeLoading(false)
      }
    }, 300)

    return () => {
      if (subRecipeTimerRef.current) {
        clearTimeout(subRecipeTimerRef.current)
      }
    }
  }, [subRecipeSearch, currentRecipeId])

  // Load initial sub-recipes on mount (show available sub-recipes without search)
  useEffect(() => {
    if (isOpen && !isEditing) {
      const loadInitialSubRecipes = async () => {
        try {
          const params = new URLSearchParams({
            subRecipesOnly: 'true',
            pageSize: '10',
          })
          const res = await fetch(`/api/recipe-builder/recipes?${params}`)
          if (res.ok) {
            const { data } = await res.json()
            const filtered = (data || []).filter((r: Recipe) => r.id !== currentRecipeId)
            if (!subRecipeSearch) {
              setSubRecipeResults(filtered)
            }
          }
        } catch {
          // Silently fail
        }
      }
      loadInitialSubRecipes()
    }
  }, [isOpen, isEditing, currentRecipeId])

  // Search USDA database with debounce
  useEffect(() => {
    if (!dbSearchQuery || dbSearchQuery.length < 2) {
      setDbSearchResults([])
      return
    }

    const timer = setTimeout(async () => {
      setDbSearchLoading(true)
      try {
        const params = new URLSearchParams({
          action: 'search',
          query: dbSearchQuery,
          pageSize: '20',
        })
        const res = await fetch(`/api/recipe-builder/usda?${params}`)
        if (res.ok) {
          const { data } = await res.json()
          setDbSearchResults(data || [])
        }
      } catch (error) {
        console.error('Failed to search USDA:', error)
      } finally {
        setDbSearchLoading(false)
      }
    }, 300)

    return () => clearTimeout(timer)
  }, [dbSearchQuery])

  // Handle selecting a sub-recipe as ingredient
  const handleSelectSubRecipe = useCallback((recipe: Recipe) => {
    setSelectedSubRecipe(recipe)
    setSelectedDbIngredient(null)

    // Compute per-100g nutrition from sub-recipe
    const nutrients = computePer100gFromRecipe(recipe)

    // Get composite ingredient names for label display
    const compositeIngredients = (recipe.ingredients || []).map((i) => i.name)

    onChange({
      name: recipe.name,
      source: 'SUB_RECIPE',
      sub_recipe_id: recipe.id,
      ingredient_id: recipe.id,
      nutrients,
      allergens: recipe.allergens || [],
      may_contain_allergens: recipe.may_contain_allergens || [],
      composite_ingredients: compositeIngredients,
      quantity: 100,
      unit: 'G',
      yield_percent: 100,
      cost: recipe.costs?.total_cost || 0,
    })

    setActiveTab('basic')
  }, [onChange])

  // Handle selecting ingredient from USDA database
  const handleSelectDbIngredient = useCallback((food: USDAIngredient) => {
    setSelectedDbIngredient(food)
    setSelectedSubRecipe(null)

    // Convert USDA nutrients to Recipe Builder format (normalize units to lowercase)
    const nutrients: Record<string, NutrientValue> = {}
    Object.entries(food.nutrients).forEach(([name, value]) => {
      nutrients[name] = {
        quantity: value.quantity,
        unit: value.unit.toLowerCase(),
      }
    })

    onChange({
      name: food.name,
      source: 'USDA',
      ingredient_id: food.fdcId.toString(),
      sub_recipe_id: null,
      nutrients,
      allergens: [], // USDA doesn't provide allergen data
      composite_ingredients: [],
      quantity: 100, // Default to 100g
      unit: 'G',
      yield_percent: 100,
    })

    // Switch to basic tab to let user adjust quantity
    setActiveTab('basic')
  }, [onChange])

  // Handle selecting ingredient from user's saved ingredients
  const handleSelectUserIngredient = useCallback((userIng: Ingredient) => {
    setSelectedSubRecipe(null)
    setSelectedDbIngredient(null)

    onChange({
      name: userIng.name,
      source: userIng.source,
      ingredient_id: userIng.ingredient_id,
      sub_recipe_id: null,
      nutrients: userIng.nutrients,
      allergens: userIng.allergens,
      may_contain_allergens: userIng.may_contain_allergens,
      composite_ingredients: [],
      quantity: 100, // Default to 100g
      unit: 'G',
      yield_percent: userIng.yield_percent || 100,
      cost: userIng.cost || 0,
    })

    // Switch to basic tab to let user adjust quantity
    setActiveTab('basic')
  }, [onChange])

  // Validation
  const canSave = ingredient.name.trim() !== '' && ingredient.quantity > 0

  // Nutrient handlers
  const handleNutrientChange = (nutrientName: string, quantity: number, unit: string) => {
    if (isSubRecipeIngredient) return // Read-only for sub-recipes
    const updatedNutrients = { ...ingredient.nutrients }
    if (quantity > 0) {
      updatedNutrients[nutrientName] = { quantity, unit }
    } else {
      delete updatedNutrients[nutrientName]
    }
    onChange({ nutrients: updatedNutrients })
  }

  // Allergen handlers
  const handleAllergenToggle = (allergen: string) => {
    if (isSubRecipeIngredient) return // Read-only for sub-recipes
    const currentAllergens = ingredient.allergens || []
    const updated = currentAllergens.includes(allergen)
      ? currentAllergens.filter((a) => a !== allergen)
      : [...currentAllergens, allergen]
    onChange({ allergens: updated })
  }

  const handleMayContainToggle = (allergen: string) => {
    if (isSubRecipeIngredient) return // Read-only for sub-recipes
    const current = ingredient.may_contain_allergens || []
    const updated = current.includes(allergen)
      ? current.filter((a) => a !== allergen)
      : [...current, allergen]
    onChange({ may_contain_allergens: updated })
  }

  // Filter nutrients and allergens by search
  const filteredNutrients = NUTRIENTS.filter((n) =>
    n.name.toLowerCase().includes(nutrientSearch.toLowerCase())
  )

  const filteredAllergens = ALLERGENS.filter((a) =>
    a.toLowerCase().includes(allergenSearch.toLowerCase())
  )

  const filteredMayContain = MAY_CONTAIN_ALLERGENS.filter((a) =>
    a.toLowerCase().includes(allergenSearch.toLowerCase())
  )

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>
            {isEditing ? 'Edit Ingredient' : 'Add New Ingredient'}
          </DialogTitle>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col min-h-0">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="search" disabled={isEditing}>
              <Database className="w-4 h-4 mr-1" />
              Search
            </TabsTrigger>
            <TabsTrigger value="basic">Basic Info</TabsTrigger>
            <TabsTrigger value="nutrients">Nutrients</TabsTrigger>
            <TabsTrigger value="allergens">Allergens</TabsTrigger>
          </TabsList>

          {/* Search Database Tab */}
          <TabsContent value="search" className="flex-1 flex flex-col min-h-0 mt-4 overflow-auto">
            <div className="space-y-4">
              {/* Sub-Recipes Section */}
              <div className="space-y-2">
                <h4 className="font-medium text-sm flex items-center gap-2">
                  <ChefHat className="w-4 h-4" />
                  Sub-Recipes
                </h4>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder="Search sub-recipes..."
                    value={subRecipeSearch}
                    onChange={(e) => setSubRecipeSearch(e.target.value)}
                    className="pl-9 h-9"
                  />
                </div>

                {selectedSubRecipe && (
                  <div className="bg-purple-50 dark:bg-purple-950 border border-purple-200 dark:border-purple-800 p-3 rounded-lg flex items-center gap-2">
                    <Check className="w-4 h-4 text-purple-600" />
                    <span className="text-sm text-purple-700 dark:text-purple-300">
                      Selected sub-recipe: <strong>{selectedSubRecipe.name}</strong>
                    </span>
                  </div>
                )}

                {(subRecipeResults.length > 0 || subRecipeLoading) && (
                  <div className="border rounded-lg max-h-[180px] overflow-auto">
                    {subRecipeLoading ? (
                      <div className="flex items-center justify-center h-20">
                        <Loader2 className="w-5 h-5 animate-spin" />
                      </div>
                    ) : (
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Recipe</TableHead>
                            <TableHead className="w-24">Category</TableHead>
                            <TableHead className="w-24 text-right">Yield</TableHead>
                            <TableHead className="w-20"></TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {subRecipeResults.map((recipe) => (
                            <TableRow
                              key={recipe.id}
                              className={`cursor-pointer hover:bg-muted/50 ${
                                selectedSubRecipe?.id === recipe.id ? 'bg-purple-50 dark:bg-purple-950' : ''
                              }`}
                              onClick={() => handleSelectSubRecipe(recipe)}
                            >
                              <TableCell>
                                <div className="font-medium">{recipe.name}</div>
                                <div className="text-xs text-muted-foreground">
                                  {(recipe.ingredients || []).length} ingredients
                                </div>
                              </TableCell>
                              <TableCell>
                                <Badge variant="secondary" className="text-xs">
                                  {recipe.category || '--'}
                                </Badge>
                              </TableCell>
                              <TableCell className="text-right text-sm">
                                {recipe.serving?.total_yield_grams || '--'} g
                              </TableCell>
                              <TableCell>
                                {selectedSubRecipe?.id === recipe.id ? (
                                  <Badge variant="default" className="bg-purple-600">
                                    <Check className="w-3 h-3 mr-1" />
                                    Selected
                                  </Badge>
                                ) : (
                                  <Button variant="ghost" size="sm">
                                    Use
                                  </Button>
                                )}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    )}
                  </div>
                )}

                {subRecipeResults.length === 0 && !subRecipeLoading && subRecipeSearch.length >= 2 && (
                  <div className="text-sm text-muted-foreground">
                    No sub-recipes found. Mark a recipe as &quot;sub-recipe&quot; in its details to make it available here.
                  </div>
                )}

                {subRecipeResults.length === 0 && !subRecipeLoading && subRecipeSearch.length < 2 && (
                  <div className="text-xs text-muted-foreground">
                    No sub-recipes available. Mark a recipe as &quot;sub-recipe&quot; in its details tab to use it as an ingredient.
                  </div>
                )}
              </div>

              {/* My Ingredients Section */}
              {userIngredients.length > 0 && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <h4 className="font-medium text-sm">My Ingredients ({userIngredients.length})</h4>
                  </div>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      placeholder="Search my ingredients..."
                      value={userIngredientSearch}
                      onChange={(e) => setUserIngredientSearch(e.target.value)}
                      className="pl-9 h-9"
                    />
                  </div>
                  <div className="border rounded-lg max-h-[150px] overflow-auto">
                    {filteredUserIngredients.length === 0 ? (
                      <div className="text-center py-4 text-sm text-muted-foreground">
                        No matching ingredients found
                      </div>
                    ) : (
                      <Table>
                        <TableBody>
                          {filteredUserIngredients.map((userIng) => (
                            <TableRow
                              key={userIng.ingredient_id}
                              className="cursor-pointer hover:bg-muted/50"
                              onClick={() => handleSelectUserIngredient(userIng)}
                            >
                              <TableCell className="py-2">
                                <div className="font-medium">{userIng.name}</div>
                              </TableCell>
                              <TableCell className="py-2 w-20">
                                <Badge variant={userIng.source === 'USDA' ? 'default' : 'secondary'} className="text-xs">
                                  {userIng.source}
                                </Badge>
                              </TableCell>
                              <TableCell className="py-2 text-right w-24">
                                {userIng.nutrients?.['Energy']?.quantity?.toFixed(0) || '--'} kcal
                              </TableCell>
                              <TableCell className="py-2 w-16">
                                <Button variant="ghost" size="sm" className="h-7">
                                  Use
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    )}
                  </div>
                </div>
              )}

              {/* USDA Search Section */}
              <div className="space-y-2">
                <h4 className="font-medium text-sm flex items-center gap-2">
                  <Database className="w-4 h-4" />
                  Search USDA Database
                </h4>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder="Search USDA ingredients (e.g., chicken breast, olive oil)..."
                    value={dbSearchQuery}
                    onChange={(e) => setDbSearchQuery(e.target.value)}
                    className="pl-9"
                  />
                </div>

                {selectedDbIngredient && (
                  <div className="bg-green-50 dark:bg-green-950 border border-green-200 dark:border-green-800 p-3 rounded-lg flex items-center gap-2">
                    <Check className="w-4 h-4 text-green-600" />
                    <span className="text-sm text-green-700 dark:text-green-300">
                      Selected: <strong>{selectedDbIngredient.name}</strong>
                    </span>
                  </div>
                )}

                <div className="text-sm text-muted-foreground">
                  {dbSearchQuery.length < 2
                    ? 'Type at least 2 characters to search USDA'
                    : dbSearchLoading
                    ? 'Searching...'
                    : `${dbSearchResults.length} results found`}
                </div>
              </div>

              {/* USDA Results */}
              {(dbSearchResults.length > 0 || dbSearchLoading) && (
                <div className="border rounded-lg max-h-[250px] overflow-auto">
                  {dbSearchLoading ? (
                    <div className="flex items-center justify-center h-32">
                      <Loader2 className="w-6 h-6 animate-spin" />
                    </div>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Ingredient</TableHead>
                          <TableHead className="w-20">Type</TableHead>
                          <TableHead className="w-24 text-right">Calories</TableHead>
                          <TableHead className="w-24 text-right">Protein</TableHead>
                          <TableHead className="w-20"></TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {dbSearchResults.map((food) => (
                          <TableRow
                            key={food.id}
                            className={`cursor-pointer hover:bg-muted/50 ${
                              selectedDbIngredient?.id === food.id ? 'bg-green-50 dark:bg-green-950' : ''
                            }`}
                            onClick={() => handleSelectDbIngredient(food)}
                          >
                            <TableCell>
                              <div className="font-medium">{food.name}</div>
                              <div className="text-xs text-muted-foreground">
                                {food.foodCategory || 'Unknown category'}
                              </div>
                            </TableCell>
                            <TableCell>
                              <Badge variant="secondary" className="text-xs">
                                {food.dataType}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-right">
                              {food.nutrients['Energy']?.quantity?.toFixed(0) || '--'} kcal
                            </TableCell>
                            <TableCell className="text-right">
                              {food.nutrients['Protein']?.quantity?.toFixed(1) || '--'} g
                            </TableCell>
                            <TableCell>
                              {selectedDbIngredient?.id === food.id ? (
                                <Badge variant="default" className="bg-green-600">
                                  <Check className="w-3 h-3 mr-1" />
                                  Selected
                                </Badge>
                              ) : (
                                <Button variant="ghost" size="sm">
                                  Select
                                </Button>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </div>
              )}

              <div className="text-sm space-y-2">
                <p className="text-muted-foreground">All nutrition values are per 100g. After selecting, adjust the quantity in Basic Info.</p>
                <div className="bg-amber-50 dark:bg-amber-950 border border-amber-200 dark:border-amber-800 p-2 rounded-lg text-amber-700 dark:text-amber-300">
                  <strong>Note:</strong> USDA does not provide allergen data. Please add allergens manually in the Allergens tab.
                </div>
              </div>
            </div>
          </TabsContent>

          {/* Basic Info Tab */}
          <TabsContent value="basic" className="flex-1 overflow-auto space-y-4 mt-4">
            {isSubRecipeIngredient && (
              <div className="bg-purple-50 dark:bg-purple-950 border border-purple-200 dark:border-purple-800 p-3 rounded-lg flex items-center gap-2">
                <ChefHat className="w-4 h-4 text-purple-600" />
                <span className="text-sm text-purple-700 dark:text-purple-300">
                  This ingredient is a <strong>sub-recipe</strong>. Nutrition and allergens are inherited from the referenced recipe.
                </span>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="ing-name">Ingredient Name *</Label>
              <Input
                id="ing-name"
                value={ingredient.name}
                onChange={(e) => onChange({ name: e.target.value })}
                placeholder="Enter ingredient name"
                disabled={isSubRecipeIngredient}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="ing-quantity">Quantity *</Label>
                <Input
                  id="ing-quantity"
                  type="number"
                  min="0"
                  step="0.01"
                  value={ingredient.quantity || ''}
                  onChange={(e) => onChange({ quantity: parseFloat(e.target.value) || 0 })}
                  placeholder="0"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="ing-unit">Unit</Label>
                <Select
                  value={ingredient.unit}
                  onValueChange={(v) => onChange({ unit: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {UNITS.map((unit) => (
                      <SelectItem key={unit} value={unit}>{unit}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="ing-yield">Yield Percent</Label>
                <div className="flex items-center gap-2">
                  <Input
                    id="ing-yield"
                    type="number"
                    min="0"
                    max="100"
                    value={ingredient.yield_percent}
                    onChange={(e) => onChange({ yield_percent: parseFloat(e.target.value) || 100 })}
                  />
                  <span className="text-muted-foreground">%</span>
                </div>
                <p className="text-xs text-muted-foreground">
                  Percentage retained after cooking
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="ing-cost">Cost per Unit (AED)</Label>
                <Input
                  id="ing-cost"
                  type="number"
                  min="0"
                  step="0.01"
                  value={ingredient.cost || ''}
                  onChange={(e) => onChange({ cost: parseFloat(e.target.value) || 0 })}
                  placeholder="0.00"
                />
              </div>
            </div>

            {!isSubRecipeIngredient && (
              <div className="bg-blue-50 dark:bg-blue-950 p-3 rounded-lg">
                <p className="text-sm text-blue-700 dark:text-blue-300">
                  Nutrient values should be entered per 100g of ingredient in the Nutrients tab.
                </p>
              </div>
            )}
          </TabsContent>

          {/* Nutrients Tab */}
          <TabsContent value="nutrients" className="flex-1 flex flex-col min-h-0 mt-4">
            {isSubRecipeIngredient && (
              <div className="bg-purple-50 dark:bg-purple-950 border border-purple-200 dark:border-purple-800 p-3 rounded-lg flex items-center gap-2 mb-4">
                <Lock className="w-4 h-4 text-purple-600" />
                <span className="text-sm text-purple-700 dark:text-purple-300">
                  Nutrients are computed from the sub-recipe and cannot be edited directly.
                </span>
              </div>
            )}

            <div className="relative mb-4">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search nutrients..."
                value={nutrientSearch}
                onChange={(e) => setNutrientSearch(e.target.value)}
                className="pl-8"
              />
            </div>

            <div className="flex-1 overflow-auto border rounded-lg">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="sticky top-0 bg-background">Nutrient</TableHead>
                    <TableHead className="sticky top-0 bg-background w-32">Per 100g</TableHead>
                    <TableHead className="sticky top-0 bg-background w-20">Unit</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredNutrients.map((nutrient) => {
                    const value = ingredient.nutrients?.[nutrient.name]
                    return (
                      <TableRow key={nutrient.name}>
                        <TableCell className="py-2">{nutrient.name}</TableCell>
                        <TableCell className="py-2">
                          {isSubRecipeIngredient ? (
                            <span className="text-sm font-medium">
                              {value?.quantity?.toFixed(2) || '0'}
                            </span>
                          ) : (
                            <Input
                              type="number"
                              min="0"
                              step="0.01"
                              value={value?.quantity || ''}
                              onChange={(e) =>
                                handleNutrientChange(
                                  nutrient.name,
                                  parseFloat(e.target.value) || 0,
                                  nutrient.unit
                                )
                              }
                              className="h-8"
                              placeholder="0"
                            />
                          )}
                        </TableCell>
                        <TableCell className="py-2 text-muted-foreground">
                          {nutrient.unit}
                        </TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          {/* Allergens Tab */}
          <TabsContent value="allergens" className="flex-1 flex flex-col min-h-0 mt-4">
            {isSubRecipeIngredient && (
              <div className="bg-purple-50 dark:bg-purple-950 border border-purple-200 dark:border-purple-800 p-3 rounded-lg flex items-center gap-2 mb-4">
                <Lock className="w-4 h-4 text-purple-600" />
                <span className="text-sm text-purple-700 dark:text-purple-300">
                  Allergens are inherited from the sub-recipe and cannot be edited directly.
                </span>
              </div>
            )}

            <div className="flex gap-2 mb-4">
              <Button
                variant={allergenTab === 'allergens' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setAllergenTab('allergens')}
              >
                Allergens ({(ingredient.allergens || []).length})
              </Button>
              <Button
                variant={allergenTab === 'maycontain' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setAllergenTab('maycontain')}
              >
                May Contain ({(ingredient.may_contain_allergens || []).length})
              </Button>
            </div>

            <div className="relative mb-4">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search allergens..."
                value={allergenSearch}
                onChange={(e) => setAllergenSearch(e.target.value)}
                className="pl-8"
              />
            </div>

            <div className="flex-1 overflow-auto border rounded-lg p-4">
              {allergenTab === 'allergens' ? (
                <div className="grid grid-cols-2 gap-2">
                  {filteredAllergens.map((allergen) => (
                    <div key={allergen} className="flex items-center space-x-2">
                      <Checkbox
                        id={`allergen-${allergen}`}
                        checked={(ingredient.allergens || []).includes(allergen)}
                        onCheckedChange={() => handleAllergenToggle(allergen)}
                        disabled={isSubRecipeIngredient}
                      />
                      <Label
                        htmlFor={`allergen-${allergen}`}
                        className={`text-sm cursor-pointer ${isSubRecipeIngredient ? 'opacity-60' : ''}`}
                      >
                        {allergen}
                      </Label>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-2">
                  {filteredMayContain.map((allergen) => (
                    <div key={allergen} className="flex items-center space-x-2">
                      <Checkbox
                        id={`maycontain-${allergen}`}
                        checked={(ingredient.may_contain_allergens || []).includes(allergen)}
                        onCheckedChange={() => handleMayContainToggle(allergen)}
                        disabled={isSubRecipeIngredient}
                      />
                      <Label
                        htmlFor={`maycontain-${allergen}`}
                        className={`text-sm cursor-pointer ${isSubRecipeIngredient ? 'opacity-60' : ''}`}
                      >
                        {allergen}
                      </Label>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>

        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={onSave} disabled={!canSave}>
            {isEditing ? 'Update Ingredient' : 'Add Ingredient'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
